import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { logger } from '@parrot-software/observability';
import {
  BlockDetailActions,
  BlockDetailHeader,
  BlockDetailSkeleton,
  BlockDetailTabs,
} from '@subscriptions/presentation/components/specific/BlockDetail';
import { ConfiguringState, ErrorState, PageContent, PageLayout } from '@subscriptions/presentation/components/base';
import {
  useBlocksConfiguring,
  useSubscriptionBlock,
  useSubscriptionAnalytics,
} from '@subscriptions/presentation/hooks';
import { isPromoBlock } from '@subscriptions/presentation/components/specific/SubscriptionsList/utils';

const subLog = logger.forDomain('SUB', { subdomain: 'lifecycle' });

/**
 * SubscriptionBlockDetailPage - Page shell for a single subscription block
 *
 * Reads `blockUuid` from URL params and fetches block data via `useSubscriptionBlock`.
 * Shows a full-page skeleton while loading, a full-page error when the fetch fails,
 * or the header + tabs once data is available.
 *
 * @returns {React.ReactElement} Block detail page
 */
export const SubscriptionBlockDetailPage = () => {
  const { t } = useTranslation('subscriptions');
  const history = useHistory();
  const { blockUuid } = useParams<{ blockUuid: string }>();
  const { data: block, isLoading, isFetching, isError, refetch } = useSubscriptionBlock(blockUuid);
  const { isAnyConfiguring, isFetching: isFetchingBlocks, refetch: refetchBlocks } = useBlocksConfiguring();
  const { trackDetailViewed } = useSubscriptionAnalytics();

  const isBlockConfiguring = Boolean(block && !block.configEnabled);
  const areActionsBlocked = isAnyConfiguring && !isBlockConfiguring;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [blockUuid]);

  const viewLogged = useRef('');
  useEffect(() => {
    if (!block || viewLogged.current === blockUuid) return;
    viewLogged.current = blockUuid;
    subLog.info('viewSubscriptionBlockDetail', {
      'block.uuid': blockUuid,
      'store.count': block.stores.length,
      'concept.count': block.stores.reduce((count, store) => count + store.concepts.length, 0),
      isGrouped: block.stores.length > 1,
      isPromo: isPromoBlock(block),
    });
    trackDetailViewed({
      blockUuid,
      customerState: block.customerState,
      isGrouped: block.stores.length > 1,
      storesCount: block.stores.length,
    });
  }, [block, blockUuid, trackDetailViewed]);

  const handleRetry = () => {
    subLog.warn('retrySubscriptionBlockDetail', { 'block.uuid': blockUuid });
    refetch();
  };

  const handleRefreshConfiguring = () => {
    subLog.info('refreshConfiguringSubscriptionBlockDetail', { 'block.uuid': blockUuid });
    refetch();
    refetchBlocks();
  };

  if (isLoading) {
    return (
      <PageLayout>
        <BlockDetailSkeleton />
      </PageLayout>
    );
  }

  if (isError && !block) {
    return (
      <PageLayout>
        <PageContent className="px-6 py-4">
          <ErrorState
            description={t('blockDetail.error.description')}
            onRetry={handleRetry}
            retryLabel={t('blockDetail.error.retry')}
            title={t('blockDetail.error.title')}
          />
          <div className="flex justify-center">
            <button
              className="text-sm text-muted-foreground underline hover:text-foreground"
              onClick={() => history.push('/subscriptions')}
              type="button"
            >
              {t('blockDetail.error.goBack')}
            </button>
          </div>
        </PageContent>
      </PageLayout>
    );
  }

  if (isBlockConfiguring) {
    return (
      <PageLayout>
        <PageContent className="min-w-0">
          <BlockDetailHeader name={block?.name ?? ''} />
          <div className="px-6 pt-2 pb-6">
            <ConfiguringState
              description={t('configuring.description')}
              isRefreshing={isFetching || isFetchingBlocks}
              onRefresh={handleRefreshConfiguring}
              refreshLabel={t('configuring.refresh')}
              title={t('configuring.title')}
            />
          </div>
        </PageContent>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageContent className="min-w-0">
        <BlockDetailHeader
          actions={block && <BlockDetailActions areActionsBlocked={areActionsBlocked} block={block} />}
          name={block?.name ?? ''}
        />
        <BlockDetailTabs block={block} isError={isError} isLoading={isLoading} onRetry={handleRetry} />
      </PageContent>
    </PageLayout>
  );
};
